import { useEffect } from 'react';
import * as THREE from 'three';

export default function useCameraControls({ camera, currentCameraConfig }) {
  useEffect(() => {
    if (!camera) {
      return () => {};
    }

    let lastTime = performance.now();
    let requestID;
    let isKeyPressed = false;
    const keysPressed = {
      KeyW: false,
      KeyS: false,
      KeyA: false,
      KeyD: false,
      KeyQ: false,
      KeyE: false,
      ArrowUp: false,
      ArrowDown: false,
      ArrowLeft: false,
      ArrowRight: false,
    };

    function onKeyDown(delta) {
      if (!camera) return;

      const isTransformControlsActive = () => {
        return (window as any).isTransformControlsDragging === true;
      };

      if (isTransformControlsActive()) {
        return;
      }

      const moveVector = new THREE.Vector3(0, 0, 0);
      const {
        KeyW,
        KeyA,
        KeyS,
        KeyD,
        KeyQ,
        KeyE,
        ArrowUp,
        ArrowDown,
        ArrowLeft,
        ArrowRight,
      } = keysPressed;

      if (!Object.values(keysPressed).some((value) => value)) {
        return;
      }

      if (KeyW || ArrowUp) {
        moveVector.add(new THREE.Vector3(0, 0, -1));
      }
      if (KeyS || ArrowDown) {
        moveVector.add(new THREE.Vector3(0, 0, 1));
      }
      if (KeyA || ArrowLeft) {
        moveVector.add(new THREE.Vector3(-1, 0, 0));
      }
      if (KeyD || ArrowRight) {
        moveVector.add(new THREE.Vector3(1, 0, 0));
      }
      if (KeyQ) {
        moveVector.add(new THREE.Vector3(0, 1, 0));
      }
      if (KeyE) {
        moveVector.add(new THREE.Vector3(0, -1, 0));
      }

      moveVector.applyQuaternion(camera.quaternion);
      moveVector.multiplyScalar(currentCameraConfig.minSpeed * delta);
      camera.position.add(moveVector);
      camera.updateProjectionMatrix();
    }

    function update() {
      if (!isKeyPressed) {
        return;
      }
      requestID = requestAnimationFrame(update);
      const time = performance.now();
      let delta = (time - lastTime) / 1000;
      lastTime = time;

      if (delta > 0.1) {
        delta = 0.1;
      }

      onKeyDown(delta);
    }

    function handleKeyDown(event) {
      if (
        (event.target.tagName === 'INPUT' &&
          event.target.className.includes('ant-input')) ||
        event.target.tagName === 'TEXTAREA'
      ) {
        event.stopPropagation();
        return;
      }
      keysPressed[event.code] = true;
      isKeyPressed = true;
      update();
      event.preventDefault();
    }

    function handleKeyUp(event) {
      keysPressed[event.code] = false;
      isKeyPressed = Object.values(keysPressed).some((value) => value);
      if (!isKeyPressed) {
        cancelAnimationFrame(requestID);
      }
      event.preventDefault();
    }

    function handleBlur() {
      cancelAnimationFrame(requestID);
    }

    function handleFocus() {
      if (isKeyPressed) {
        update();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('blur', handleBlur);
    document.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('blur', handleBlur);
      document.removeEventListener('focus', handleFocus);
      cancelAnimationFrame(requestID);
    };
  }, [camera, currentCameraConfig]);

  return [];
}
